export function centsToDollars(amountInCents: number | null): number | null {
  return amountInCents != null ? +(amountInCents / 100).toFixed(2) : null;
}
export function centsToDollarsFormatted(amountInCents: number | null): string | null {
  if (typeof amountInCents !== "number") {
    return null;
  }
  const negative = amountInCents < 0;
  const dollars = centsToDollars(amountInCents);

  return dollars !== null
    ? `${negative ? "-" : ""}$` +
        Math.abs(dollars).toLocaleString("en-US", {
          minimumFractionDigits: 2
        })
    : null;
}

/**
 * Formats a string value for currency input fields with thousands separators.
 * Used in form inputs like NumberFormatBase.
 * Example: 100000 -> "1,000.00"
 * @param value - The string value from the input field (in cents)
 * @param decimalPlaces - Number of decimal places to display (default: 2)
 * @returns The formatted string without currency symbol
 */
export function currencyFormatter(value: string, decimalPlaces: number = 2) {
  if (!value) return "";
  const amount = (Number(value) || 0) / 100;
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
}

/**
 * Formats a string value for currency input fields with thousands separators and no decimals.
 * Used in form inputs like NumberFormatBase when decimal places are not needed.
 * This formatter works with the raw numeric values without cents conversion.
 * Example: 70000 -> "70,000"
 * @param value - The string value from the input field (raw numeric value)
 * @returns The formatted string without currency symbol and no decimals
 */
export function currencyFormatterNoDecimals(value: string) {
  if (!value) return "";
  const amount = Number(value) || 0;
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Formats a string value (representing cents) as dollars with thousands separators and $ symbol.
 * @param value - The string value from the input field (in cents)
 * @returns The formatted string with $ symbol
 */
export function currencyFormatterWithPrefix(value: string): string {
  if (!value) return "";
  const number = (Number(value) || 0) / 100;
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

/**
 * Formats a number for display with thousands separators.
 * Used for displaying currency values in read-only contexts.
 * @param value - The number to format (in cents)
 * @param decimalPlaces - Number of decimal places to display (default: 2)
 * @returns The formatted string without currency symbol
 */
export function formatDisplayCurrency(value: number | null | undefined, decimalPlaces: number = 2): string {
  if (value == null) return "";
  const number = Number(value);
  return number.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
}

/**
 * Checks if a value represents a valid price in dollars
 * @param value - The value of the cell
 * @returns boolean indicating if the value is a valid dollar price
 */
export function isDollarPrice(value: any): boolean {
  if (typeof value !== "string") {
    return false;
  }
  // Remove dollar sign and spaces
  const cleanValue = value.replace(/[$\s]/g, "");

  // Regular expression for dollar price format
  // Allows: optional commas for thousands, optional decimal places
  const priceRegex = /^(\d{1,3}(,\d{3})*)?(\.\d{0,2})?$/;

  return priceRegex.test(cleanValue);
}

/**
 * Formats a total compensation value with conditional decimal places.
 * Shows cents for values between $1.00 - $999.99, no cents otherwise.
 * Returns "--" for null/undefined values.
 * @param value - The number to format (in dollars, non-negative)
 * @returns The formatted currency string with conditional cents
 */
export function formatTotalCompensation(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "--";
  }

  // Show cents if value is between $1.00 and $999.99
  const shouldShowCents = value >= 1 && value < 1000;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: shouldShowCents ? 2 : 0,
    maximumFractionDigits: shouldShowCents ? 2 : 0
  }).format(value);
}
